import { Params } from '@angular/router';
import { parseIntWithDefault } from '../../util/parse-int-with-default';
import {
  ColumnFilter,
  GridSort,
  PaginationState,
  SortDirection,
} from '../../table/components/table/table.types';
import { Observable } from 'rxjs';
import { SerializedRouterStateSnapshot } from '@ngrx/router-store';

// Configurable query param names
export interface GridDataQueryParamConfig {
  page: string;
  pageSize: string;
  q: string;
  sortBy: string;
  sortDirection: string;
}

export const DEFAULT_GRID_DATA_QUERY_PARAMS_CONFIG: GridDataQueryParamConfig = {
  page: 'page',
  pageSize: 'pageSize',
  q: 'q',
  sortBy: 'sortBy',
  sortDirection: 'sort',
};

export const DEFAULT_PAGE_SIZE = 10;

export function gridSortToQueryParams(
  sort: GridSort,
  queryParamsConfig: GridDataQueryParamConfig,
): Params {
  return sort.sortDirection === SortDirection.none
    ? {
        [queryParamsConfig.sortDirection]: null,
        [queryParamsConfig.sortBy]: null,
      }
    : {
        [queryParamsConfig.sortDirection]: sort.sortDirection,
        [queryParamsConfig.sortBy]: sort.sortBy,
      };
}

/**
 * Data that a fetching function must be able to fetch.
 */
export interface FetchedData<T> {
  rowData: T[];
  rowCount: number;
}

export type FetchingFn<TData> = (
  offset: number,
  limit: number,
  filter: ColumnFilter,
  sort: GridSort,
) => Observable<FetchedData<TData>>;

export type RequestDataFn = (
  offset: number,
  limit: number,
  filter: ColumnFilter,
  sort: GridSort,
) => void;

export interface GridPageData<TData> {
  rowData: TData[];
  filter: ColumnFilter;
  sort: GridSort;
  paginationState: PaginationState;
  loading: boolean;
}

export function createInitialPageData<TData>(): {
  gridData: GridPageData<TData>;
} {
  return {
    gridData: {
      loading: false,
      rowData: [],
      paginationState: { currentPage: 1, recordsPerPage: 10, totalRecords: 0 },
      filter: [],
      sort: { sortDirection: SortDirection.none },
    },
  };
}

export interface GridPageDataState<TData> {
  loading: boolean;
  rowData: TData[];
  totalRecords: number;
}

export function createInitialPageDataState<TData>(): GridPageDataState<TData> {
  return {
    loading: false,
    rowData: [],
    totalRecords: 0,
  };
}

export function filterToQueryParam(filter: ColumnFilter): string {
  return filter
    .map(({ colId, filter }) => `${colId}=${encodeURIComponent(filter)}`)
    .join(',');
}

export function filterEq(
  filter1: ColumnFilter,
  filter2: ColumnFilter,
): boolean {
  if (filter1 === filter2) {
    // Optimization
    return true;
  }
  const filterToMap = (columnFilter: ColumnFilter) => {
    const map = new Map<string, string>();
    columnFilter.forEach(({ colId, filter }) => map.set(colId, filter));
    return map;
  };
  const mapContained = (map1: Map<string, string>, map2: Map<string, string>) =>
    [...map1.keys()].every(key => map1.get(key) === map2.get(key));
  const map1 = filterToMap(filter1);
  const map2 = filterToMap(filter2);
  return mapContained(map1, map2) && mapContained(map2, map1);
}

export function filterFromQueryParam(queryParam: string): ColumnFilter {
  const columnFilter: ColumnFilter = [];
  for (const keyValue of queryParam.split(',')) {
    const splitByEquals = keyValue.split('=');
    if (splitByEquals.length !== 2) {
      // Invalid encoded filter, return empty filter
      return [];
    }
    const [colId, filter] = splitByEquals;
    columnFilter.push({ colId, filter: decodeURIComponent(filter) });
  }
  return columnFilter;
}

export function pageDataStateToParams<TData>(
  state: GridPageDataState<TData>,
  queryParams: Params,
  queryParamsConfig: GridDataQueryParamConfig,
): GridPageData<TData> {
  const paginationState: PaginationState = {
    currentPage: parseIntWithDefault(queryParams?.[queryParamsConfig.page], 1),
    recordsPerPage: parseIntWithDefault(
      queryParams?.[queryParamsConfig.pageSize],
      DEFAULT_PAGE_SIZE,
    ),
    totalRecords: state.totalRecords,
  };
  const filterParam = queryParams?.[queryParamsConfig.q];
  const filter =
    filterParam && typeof filterParam === 'string'
      ? filterFromQueryParam(filterParam)
      : [];
  const sortParam = queryParams?.[queryParamsConfig.sortDirection];
  const sortByParam = queryParams?.[queryParamsConfig.sortBy];
  const sort = GridSort.of({
    sortDirection: sortParam,
    sortBy: sortByParam,
  });
  return {
    ...state,
    paginationState,
    filter,
    sort,
  };
}

export interface DataFeatureConfig {
  queryParamsConfig?: GridDataQueryParamConfig;
  routeFilter: (route: SerializedRouterStateSnapshot) => boolean;
}
